import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import { ReportActions } from '../../../components/ui/ReportActions';
import useAttritionReport from './useAttritionReport';
import Container from '../../../components/ui/Container/Container';
import { Row, Col, Button, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { Helpers } from '../../../utils/helpers';
import styles from './AttritionReport.module.css';

interface AttritionReportProps {
  visible?: boolean;
}

export const AttritionReport = ({ visible = true }: AttritionReportProps) => {
  const manager = useAttritionReport();

  if (!visible) return <></>;

  return (
    <Container visible={true} className={styles.reportContainer}>
      <Row className="justify-content-center">
        <Col xl={10} lg={11} md={12}>
          <div className={styles.summarySection}>
            <div className="d-flex justify-content-between align-items-end flex-wrap gap-2">
              <Form.Group>
                <Form.Label className="small mb-1">
                  <strong>Billing Year</strong>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={manager.billingYear}
                  onChange={(e) => manager.applyBillingYear(e.target.value)}
                  disabled={manager.loading}
                  style={{ width: '120px' }}>
                  {manager.billingYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => void manager.reload()}
                disabled={manager.loading}>
                <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                Refresh
              </Button>
            </div>
          </div>

          <div className={styles.resultsSection}>
            <LoadingContainer isLoading={manager.loading}>
              {manager.hasLoaded && manager.pagedResults.resultCount === 0 && (
                <div className="text-center py-4 text-muted">
                  No members with an outstanding balance for billing year {manager.billingYear}.
                </div>
              )}
              {manager.pagedResults.resultCount > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>{manager.pagedResults.resultCount}</strong> records
                    </div>
                    <ReportActions
                      data={manager.getSortedResultsForExport()}
                      columns={manager.getReportColumns()}
                      reportTitle={`Attrition Report - Billing Year ${manager.billingYear}`}
                      csvFileName={`attritionReport_${manager.billingYear}.csv`}
                      searchCriteria={manager.getSearchCriteria()}
                      size="sm"
                      variant="outline-primary"
                    />
                  </div>

                  <Table striped hover responsive className={styles.resultsTable}>
                    <thead>
                      <tr>
                        <SortableHeader text="Type" onClick={manager.changeSort} />
                        <SortableHeader
                          text="Member ID"
                          sortField="memberId"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'memberId'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Last Name"
                          sortField="lastName"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'lastName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="First Name"
                          sortField="firstName"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'firstName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Status"
                          sortField="memberStatus"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'memberStatus'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Member Type"
                          sortField="memberTypeName"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'memberTypeName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Phone"
                          sortField="phone"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'phone'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Email"
                          sortField="email"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'email'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Paid"
                          sortField="amountPaidForBillingYear"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={
                            manager.searchParameters.sortField === 'amountPaidForBillingYear'
                          }
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Balance Due"
                          sortField="balanceDue"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'balanceDue'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Last Payment"
                          sortField="lastPaymentDateUtc"
                          sortDirection={manager.searchParameters.sortDirection}
                          active={manager.searchParameters.sortField === 'lastPaymentDateUtc'}
                          onClick={manager.changeSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {manager.pagedResults.results?.map((row, rowIndex) => (
                        <tr
                          key={`${row.clubMemberId}-${row.billingYear}-${row.lastName}-${row.firstName}-${rowIndex}`}
                          className={`${styles.clickableRow} ${
                            row.isSecondaryAdult ? styles.secondaryRow : ''
                          }`}
                          onClick={() =>
                            window.open(`/members/${row.clubMemberId}`, '_blank')
                          }>
                          <td>{row.isSecondaryAdult ? 'Secondary' : 'Primary'}</td>
                          <td>{row.memberId}</td>
                          <td>{row.lastName}</td>
                          <td>{row.firstName}</td>
                          <td>{row.memberStatus}</td>
                          <td>{row.memberTypeName}</td>
                          <td>{row.phone || row.cellPhone || '-'}</td>
                          <td>{row.email || '-'}</td>
                          {/* Invoice money belongs to the household and is shown once, on the
                              primary row — repeating it on the secondary row double-counts the
                              invoice when the column is summed (e.g. in an exported CSV). */}
                          <td>
                            {row.isSecondaryAdult
                              ? '-'
                              : Helpers.formatCurrency(row.amountPaidForBillingYear)}
                          </td>
                          <td className={styles.balanceCell}>
                            {row.isSecondaryAdult ? '-' : Helpers.formatCurrency(row.balanceDue)}
                          </td>
                          <td>
                            {!row.isSecondaryAdult && row.lastPaymentDateUtc
                              ? Helpers.toShortDateFromUtc(row.lastPaymentDateUtc)
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="pt-4">
                    <PagingControls
                      page={manager.pagedResults.currentPage ?? 1}
                      pageCount={manager.pagedResults.pageCount ?? 1}
                      maxPlusOrMinusPagesToShow={3}
                      onPageChange={manager.changePage}
                    />
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <div className={styles.totalsContainer}>
                      <h6 className="mb-3">Report Totals:</h6>
                      <Table size="sm" bordered className={styles.totalsTable}>
                        <tbody>
                          <tr>
                            <td>Total Invoices for Billing Year</td>
                            <td className={styles.rightAlign}>
                              {manager.totalInvoicesForBillingYear}
                            </td>
                          </tr>
                          <tr>
                            <td>Non-Renewal</td>
                            <td className={styles.rightAlign}>{manager.lostMembers}</td>
                          </tr>
                          <tr>
                            <td>Attrition %</td>
                            <td className={styles.rightAlign}>
                              {manager.attritionPercent.toFixed(1)}%
                            </td>
                          </tr>
                          <tr>
                            <td>Total Paid</td>
                            <td className={styles.rightAlign}>
                              {Helpers.formatCurrency(manager.totalPaid)}
                            </td>
                          </tr>
                          <tr className={styles.totalRow}>
                            <td>Total Balance Due</td>
                            <td className={styles.rightAlign}>
                              {Helpers.formatCurrency(manager.totalBalanceDue)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </LoadingContainer>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AttritionReport;
